/**
 * Recherche Shape (snippets/shape-search.liquid).
 * - Fenêtre <dialog> modale : piège le focus, Échap ferme, arrière-plan inerte
 * - Recherche instantanée via l'API Predictive Search (JSON), avec délai de frappe et annulation
 *   des requêtes obsolètes
 * - Navigation clavier dans les résultats (flèches, Entrée)
 * - Remplace l'ouverture de la recherche Horizon : les boutons loupe d'Horizon ouvrent cette fenêtre
 */
const DEBOUNCE = 160;
const TAX_RATE = 1.2;

/** @param {string} value */
const escapeHtml = (value) =>
  value.replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char] ?? char);

/**
 * Entoure les mots recherchés par <mark> (texte déjà échappé)
 * @param {string} text
 * @param {string} terms
 */
function highlight(text, terms) {
  const safe = escapeHtml(text);
  const words = terms
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 1)
    .map((word) => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  if (!words.length) return safe;
  return safe.replace(new RegExp(`(${words.join('|')})`, 'gi'), '<mark>$1</mark>');
}

/**
 * Ajoute un paramètre de largeur aux images du CDN Shopify
 * @param {string | undefined} url
 * @param {number} width
 */
const sizedImage = (url, width) => {
  if (!url) return '';
  const sized = new URL(url, location.origin);
  sized.searchParams.set('width', String(width));
  return sized.toString();
};

class ShapeSearch extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;
  /** @type {number | undefined} */
  #timer;
  #activeIndex = -1;
  #terms = '';

  connectedCallback() {
    this.dialog = /** @type {HTMLDialogElement} */ (this.querySelector('dialog'));
    this.input = /** @type {HTMLInputElement} */ (this.querySelector('.shape-search__input'));
    this.idle = /** @type {HTMLElement} */ (this.querySelector('[data-idle]'));
    this.results = /** @type {HTMLElement} */ (this.querySelector('[data-results]'));
    this.viewAll = /** @type {HTMLAnchorElement} */ (this.querySelector('[data-view-all]'));
    this.clearButton = /** @type {HTMLButtonElement} */ (this.querySelector('[data-action="clear"]'));

    const { locale = 'en', currency = 'EUR' } = this.dataset;
    this.money = new Intl.NumberFormat(locale, { style: 'currency', currency });

    this.input.addEventListener('input', this.#onInput);
    this.input.addEventListener('keydown', this.#onKeydown);
    this.querySelector('[data-action="close"]')?.addEventListener('click', () => this.close());
    this.clearButton.addEventListener('click', () => {
      this.input.value = '';
      this.#onInput();
      this.input.focus();
    });
    this.querySelectorAll('[data-term]').forEach((chip) =>
      chip.addEventListener('click', () => {
        this.input.value = /** @type {HTMLElement} */ (chip).dataset.term ?? '';
        this.#onInput();
        this.input.focus();
      })
    );

    // Clic sur le fond (hors panneau) : fermeture
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.close();
    });
    this.dialog.addEventListener('close', () => document.documentElement.classList.remove('shape-search-open'));
  }

  open() {
    if (this.dialog.open) return;
    this.dialog.showModal();
    document.documentElement.classList.add('shape-search-open');
    this.input.focus();
    this.input.select();
  }

  close() {
    if (!this.dialog.open) return;
    this.dialog.classList.add('is-closing');
    window.setTimeout(() => {
      this.dialog.classList.remove('is-closing');
      this.dialog.close();
    }, 180);
  }

  #onInput = () => {
    const terms = this.input.value.trim();
    this.clearButton.hidden = !terms;
    window.clearTimeout(this.#timer);

    if (!terms) {
      this.#controller?.abort();
      this.#showIdle();
      return;
    }
    this.#timer = window.setTimeout(() => this.#search(terms), DEBOUNCE);
  };

  #showIdle() {
    this.#terms = '';
    this.idle.hidden = false;
    this.results.hidden = true;
    this.viewAll.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    this.classList.remove('is-loading');
    this.#setActive(-1);
  }

  /** @param {string} terms */
  async #search(terms) {
    this.#controller?.abort();
    this.#controller = new AbortController();
    this.classList.add('is-loading');

    const url = new URL(`${this.dataset.url}.json`, location.origin);
    url.searchParams.set('q', terms);
    url.searchParams.set('resources[type]', 'product,collection,query');
    url.searchParams.set('resources[limit]', '6');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor,variants.sku');
    url.searchParams.set('resources[options][unavailable_products]', 'last');

    try {
      const response = await fetch(url, { signal: this.#controller.signal });
      const data = await response.json();
      this.#render(terms, data.resources?.results ?? {});
    } catch (error) {
      if (/** @type {Error} */ (error).name !== 'AbortError') console.error(error);
    } finally {
      this.classList.remove('is-loading');
    }
  }

  /**
   * @param {string} terms
   * @param {{ products?: any[], collections?: any[], queries?: any[] }} results
   */
  #render(terms, { products = [], collections = [], queries = [] }) {
    this.#terms = terms;
    this.idle.hidden = true;
    this.results.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');

    this.#fill(
      'queries',
      queries.slice(0, 5).map(
        (query) => `
        <li><a href="${escapeHtml(query.url)}" class="shape-search__row" data-result>
          <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true"><circle cx="7" cy="7" r="4.75" fill="none" stroke="currentColor" stroke-width="1.5"/><path d="m10.5 10.5 3.25 3.25" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
          <span>${highlight(query.text, terms)}</span>
        </a></li>`
      )
    );

    this.#fill(
      'collections',
      collections.slice(0, 5).map(
        (collection) => `
        <li><a href="${escapeHtml(collection.url)}" class="shape-search__row" data-result>
          <span class="shape-search__thumb">${
            collection.featured_image?.url
              ? `<img src="${sizedImage(collection.featured_image.url, 80)}" alt="" width="32" height="32" loading="lazy">`
              : ''
          }</span>
          <span>${highlight(collection.title, terms)}</span>
        </a></li>`
      )
    );

    this.#fill(
      'products',
      products.slice(0, 6).map((product, index) => {
        const price = Number(product.price);
        const compare = Number(product.compare_at_price_max);
        const onSale = compare > price;
        const image = product.featured_image?.url ?? product.image;
        return `
        <li style="--i: ${index};"><a href="${escapeHtml(product.url)}" class="shape-search__product${product.available ? '' : ' is-unavailable'}" data-result>
          <span class="shape-search__product-media">${
            image ? `<img src="${sizedImage(image, 200)}" alt="" width="72" height="72" loading="lazy">` : ''
          }</span>
          <span class="shape-search__product-text">
            <span class="shape-search__product-title">${highlight(product.title, terms)}</span>
            <span class="shape-search__product-price">
              <strong${onSale ? ' class="is-sale"' : ''}>${this.money.format(price)}</strong>
              <small>${escapeHtml(this.dataset.labelIncl ?? '')}</small>
              ${onSale ? `<s>${this.money.format(compare)}</s>` : ''}
              <span class="shape-search__product-ht">${this.money.format(price / TAX_RATE)} ${escapeHtml(this.dataset.labelExcl ?? '')}</span>
            </span>
          </span>
          <svg class="shape-search__product-go" width="14" height="14" viewBox="0 0 16 16" aria-hidden="true"><path d="M3 8h9.5M8.5 3.5 13 8l-4.5 4.5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </a></li>`;
      })
    );

    const empty = !products.length && !collections.length && !queries.length;
    const emptyBlock = /** @type {HTMLElement} */ (this.querySelector('[data-empty]'));
    emptyBlock.hidden = !empty;
    const emptyTitle = this.querySelector('[data-empty-title]');
    if (emptyTitle) emptyTitle.textContent = (this.dataset.labelNoResults ?? '').replace('__TERMS__', terms);

    // Lien « Voir tous les résultats »
    const searchUrl = new URL(this.dataset.searchUrl ?? '/search', location.origin);
    searchUrl.searchParams.set('q', terms);
    this.viewAll.href = searchUrl.toString();
    this.viewAll.hidden = empty;
    const label = this.viewAll.querySelector('[data-view-all-label]');
    if (label) label.textContent = (this.dataset.labelViewAll ?? '').replace('__TERMS__', terms);

    this.#setActive(-1);
  }

  /**
   * @param {string} group
   * @param {string[]} items
   */
  #fill(group, items) {
    const list = this.querySelector(`[data-list="${group}"]`);
    const block = /** @type {HTMLElement | null} */ (this.querySelector(`[data-group="${group}"]`));
    if (!list || !block) return;
    list.innerHTML = items.join('');
    block.hidden = items.length === 0;
  }

  /** Éléments navigables au clavier dans l'état affiché */
  get #items() {
    const scope = this.results.hidden ? this.idle : this.results;
    const items = /** @type {HTMLElement[]} */ ([...scope.querySelectorAll('[data-result]')]);
    if (!this.viewAll.hidden) items.push(this.viewAll);
    return items;
  }

  /** @param {number} index */
  #setActive(index) {
    const items = this.#items;
    items.forEach((item, i) => item.classList.toggle('is-active', i === index));
    this.#activeIndex = index;
    items[index]?.scrollIntoView({ block: 'nearest' });
  }

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    const items = this.#items;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!items.length) return;
      const step = event.key === 'ArrowDown' ? 1 : -1;
      this.#setActive((this.#activeIndex + step + items.length) % items.length);
    } else if (event.key === 'Enter') {
      const active = items[this.#activeIndex];
      if (active) {
        event.preventDefault();
        active.click();
      }
      // Sinon : envoi du formulaire vers la page de résultats complète
    }
  };
}

if (!customElements.get('shape-search')) {
  customElements.define('shape-search', ShapeSearch);
}

/* ---------- Ouverture : barre centrale, loupes Horizon, raccourci « / » ---------- */

const openShapeSearch = () => {
  const search = /** @type {ShapeSearch | null} */ (document.querySelector('shape-search'));
  if (!search) return false;
  search.open();
  return true;
};

// Les boutons loupe d'Horizon (mobile notamment) ouvrent la recherche Shape.
// Écoute en phase de capture sur window pour passer avant le gestionnaire d'Horizon.
window.addEventListener(
  'click',
  (event) => {
    const trigger = event.target instanceof Element ? event.target.closest('search-button button, [data-shape-search]') : null;
    if (!trigger) return;
    if (openShapeSearch()) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  },
  true
);

// Raccourci « / » (hors champs de saisie)
window.addEventListener(
  'keydown',
  (event) => {
    if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return;
    const target = event.target instanceof HTMLElement ? event.target : null;
    if (target?.closest('input, textarea, select, [contenteditable="true"]')) return;
    if (openShapeSearch()) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  },
  true
);
